
import React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tables } from "@/integrations/supabase/types";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

type WorldCategory = Tables<"world_categories">;

interface WorldCategoryFormProps {
  category?: WorldCategory;
  onSubmit: (data: Omit<WorldCategory, 'id' | 'created_at' | 'updated_at'>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const colorOptions = [
  { value: 'from-blue-400 to-blue-600', label: 'Blue Gradient', preview: 'bg-gradient-to-r from-blue-400 to-blue-600' },
  { value: 'from-green-400 to-green-600', label: 'Green Gradient', preview: 'bg-gradient-to-r from-green-400 to-green-600' },
  { value: 'from-red-400 to-red-600', label: 'Red Gradient', preview: 'bg-gradient-to-r from-red-400 to-red-600' },
  { value: 'from-yellow-400 to-yellow-600', label: 'Yellow Gradient', preview: 'bg-gradient-to-r from-yellow-400 to-yellow-600' },
  { value: 'from-purple-400 to-purple-600', label: 'Purple Gradient', preview: 'bg-gradient-to-r from-purple-400 to-purple-600' },
  { value: 'from-pink-400 to-pink-600', label: 'Pink Gradient', preview: 'bg-gradient-to-r from-pink-400 to-pink-600' },
  { value: 'from-indigo-400 to-indigo-600', label: 'Indigo Gradient', preview: 'bg-gradient-to-r from-indigo-400 to-indigo-600' },
  { value: 'from-orange-400 to-orange-600', label: 'Orange Gradient', preview: 'bg-gradient-to-r from-orange-400 to-orange-600' },
  { value: 'from-teal-400 to-teal-600', label: 'Teal Gradient', preview: 'bg-gradient-to-r from-teal-400 to-teal-600' },
  { value: 'from-cyan-400 to-cyan-600', label: 'Cyan Gradient', preview: 'bg-gradient-to-r from-cyan-400 to-cyan-600' },
];

const WorldCategoryForm: React.FC<WorldCategoryFormProps> = ({
  category,
  onSubmit,
  onCancel,
  isSubmitting = false
}) => {
  const [formData, setFormData] = React.useState({
    name: category?.name || '',
    color: category?.color || 'from-pink-400 to-pink-600',
    display_order: category?.display_order || 0,
    is_active: category?.is_active ?? true,
    seo_title: category?.seo_title || '',
    meta_description: category?.meta_description || '',
    slug: category?.slug || '',
    keywords: category?.keywords?.join(', ') || '',
    description: category?.description || '',
    icon: category?.icon || '🌍',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      keywords: formData.keywords ? formData.keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
      slug: formData.slug || formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
    };
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Category Name</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </div>

      <div>
        <Label htmlFor="color">Color Class</Label>
        <Select value={formData.color} onValueChange={(value) => setFormData({ ...formData, color: value })}>
          <SelectTrigger>
            <SelectValue placeholder="Select a color" />
          </SelectTrigger>
          <SelectContent>
            {colorOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${option.preview}`}></div>
                  <span>{option.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="display_order">Display Order</Label>
        <Input
          id="display_order"
          type="number"
          value={formData.display_order}
          onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
        />
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <RichTextEditor
          value={formData.description}
          onChange={(value) => setFormData({ ...formData, description: value })}
          placeholder="Category description with rich formatting"
          height="150px"
        />
      </div>

      <div>
        <Label htmlFor="icon">Icon (Emoji)</Label>
        <Input
          id="icon"
          value={formData.icon}
          onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
          placeholder="🌍"
          maxLength={2}
        />
      </div>

      <div>
        <Label htmlFor="seo_title">SEO Title</Label>
        <Input
          id="seo_title"
          value={formData.seo_title}
          onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
          placeholder="SEO optimized title (50-60 characters)"
          maxLength={60}
        />
        <p className="text-sm text-gray-500 mt-1">{formData.seo_title.length}/60 characters</p>
      </div>

      <div>
        <Label htmlFor="meta_description">Meta Description</Label>
        <Input
          id="meta_description"
          value={formData.meta_description}
          onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
          placeholder="SEO meta description (150-160 characters)"
          maxLength={160}
        />
        <p className="text-sm text-gray-500 mt-1">{formData.meta_description.length}/160 characters</p>
      </div>

      <div>
        <Label htmlFor="slug">URL Slug</Label>
        <Input
          id="slug"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
          placeholder="url-friendly-slug"
        />
        <p className="text-sm text-gray-500 mt-1">Preview: /learn-about-world/{formData.slug || formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}</p>
      </div>

      <div>
        <Label htmlFor="keywords">SEO Keywords</Label>
        <Input
          id="keywords"
          value={formData.keywords}
          onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
          placeholder="keyword1, keyword2, keyword3"
        />
        <p className="text-sm text-gray-500 mt-1">Separate keywords with commas</p>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
        />
        <Label htmlFor="is_active">Active</Label>
      </div>

      <div className="flex space-x-2">
        <Button type="submit" disabled={isSubmitting}>
          {category ? 'Update' : 'Create'} Category
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default WorldCategoryForm;
